import React from "react";
import { useCase } from "../../../../contexts/CaseContext";
import { Subheading } from "../../../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../design/table";
import CreateContractButton from "./CreateContractButton";
import FileActionButtons from "../../../../components/FileActionButtons";
import DeleteContractButton from "./DeleteContractButton";
import ContractsLearnMore from "../../../../components/LearnMores/ContractsLearnMore";
import { useParent } from "../../../../contexts/ParentContext";
import { DocumentIcon } from "@heroicons/react/24/outline";
import { Text } from "../../../../design/text";

/**
 * Extracts the filename from a storage path.
 * e.g., "cases/abc123/Contract_Agreement.pdf" -> "Contract_Agreement.pdf"
 */
const getFileNameFromPath = (path: string | undefined): string => {
  if (!path) return "Unknown file";
  const parts = path.split("/");
  return parts[parts.length - 1] || "Unknown file";
};

interface CaseDetailContractListProps {
  caseId: string;
}

const CaseDetailContractList: React.FC<CaseDetailContractListProps> = ({
  caseId,
}) => {
  const { contracts } = useCase();
  const { parents } = useParent();
  return (
    <>
      <div className="flex flex-wrap items-center justify-between w-full">
        <div className="flex items-center gap-1">
          <Subheading>Contracts</Subheading>
          <ContractsLearnMore />
        </div>
        <div className="flex gap-4">
          <CreateContractButton caseId={caseId} />
        </div>
      </div>
      {contracts.length === 0 && (
        <div className="py-8 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No contracts have been added yet.</p>
        </div>
      )}
      {contracts.length > 0 && (
        <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>File</TableHeader>
              <TableHeader>Parent</TableHeader>
              <TableHeader>Percent</TableHeader>
              <TableHeader className="text-right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {contracts.map((contract) => {
              const parent = parents.find(
                (parent) => parent.id === contract.parentId,
              );
              const fileName = getFileNameFromPath(contract.path);
              return (
                <TableRow key={contract.id} title={`Contract #${contract.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex-shrink-0">
                        <DocumentIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <Text className="truncate max-w-xs font-medium text-zinc-900 dark:text-zinc-100" title={fileName}>
                        {fileName}
                      </Text>
                    </div>
                  </TableCell>
                  <TableCell>{`${parent?.firstName} ${parent?.lastName}`}</TableCell>
                  <TableCell>{contract.percent}%</TableCell>
                  <TableCell className="flex items-end justify-end gap-2">
                    {contract.path && (
                      <FileActionButtons path={contract.path} />
                    )}
                    <DeleteContractButton contract={contract} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default CaseDetailContractList;
